import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, Trash2, UserPlus } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
  useTranslate,
} from "ra-core";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import type { Sale } from "@/components/atomic-crm/types";

import { InputContrasena } from "../auth/CampoContrasena";
import { formatoFechaHora } from "../negocios/formato";
import type { NevoraDataProvider } from "../datos/dataProvider";
import { AvatarUsuario, EditorFoto } from "../usuarios/FotoUsuario";
import {
  claveErrorUsuarios,
  lineasAsociado,
  validarUsuario,
  type DatosUsuario,
  type ErroresUsuario,
} from "./usuarios";

type Edicion = { modo: "alta" } | { modo: "editar"; usuario: Sale };

/** Papel en el equipo al invitar (auditoría 30/10/2026). «Closer» y «Team lead» aún no existen. */
const PAPELES_ALTA = ["setter", "consultor", "ninguno"] as const;
type PapelAlta = (typeof PAPELES_ALTA)[number];
type DatosPapelAlta = {
  rol: PapelAlta;
  recibe: boolean;
  pct: string;
  tope: string;
  comision: string;
};
const aNumeroONulo = (v: string): number | null => {
  const n = Number(v.replace(",", "."));
  return v.trim() === "" || Number.isNaN(n) ? null : n;
};

const CampoNumero = ({
  id,
  etiqueta,
  valor,
  cambiar,
}: {
  id: string;
  etiqueta: string;
  valor: string;
  cambiar: (v: string) => void;
}) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs">
      {etiqueta}
    </Label>
    <Input
      id={id}
      inputMode="decimal"
      value={valor}
      onChange={(e) => cambiar(e.target.value)}
    />
  </div>
);
/** 30: la fila de sales trae `foto` (aunque esté vacía) cuando la base ya tiene la 30. */
type SaleConFoto = Sale & { foto?: string | null };

/**
 * Ajustes → Usuarios (petición de Robert, 01/10/2026; adelanta parte de la FASE 4). Solo
 * administradores: alta con contraseña provisional (sin correo) o invitación por email
 * (necesita SMTP, tanda B), nombre, administrador sí/no y desactivar. Usa la función `users`
 * de Atomic (con los cambios de docs/parches-atomic.md); nadie se quita su propio acceso y
 * siempre queda un administrador activo.
 */
export const UsuariosAjustes = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { data: usuarios, isPending } = useGetList<SaleConFoto>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });
  const [edicion, setEdicion] = useState<Edicion | null>(null);
  const dataProvider = useDataProvider<NevoraDataProvider>();
  // Último acceso (02/10/2026): lo da la función `users`; si falla, la lista sigue igual.
  const { data: accesos } = useQuery({
    queryKey: ["sales", "ultimos-accesos"],
    queryFn: () => dataProvider.ultimosAccesos(),
    staleTime: 60 * 1000,
  });

  // 30: foto, email y borrar, solo si la base ya la tiene.
  const con30 = (usuarios ?? []).some((u) => "foto" in u);

  return (
    <section className="rounded-lg border bg-card px-4 py-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="titulo-seccion">
            {translate("nevora.usuarios.titulo")}
          </h2>
          <p className="text-xs text-muted-foreground">
            {translate("nevora.usuarios.ayuda")}
          </p>
        </div>
        <Button onClick={() => setEdicion({ modo: "alta" })}>
          <UserPlus className="size-4 mr-1.5" />
          {translate("nevora.usuarios.nuevo")}
        </Button>
      </div>
      {isPending ? (
        <p className="text-sm text-muted-foreground" role="status">
          {translate("nevora.usuarios.cargando")}
        </p>
      ) : (
        <ul className="divide-y">
          {(usuarios ?? []).map((u) => (
            <li
              key={u.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5"
            >
              {con30 ? (
                <AvatarUsuario
                  nombre={
                    `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() ||
                    u.email
                  }
                  foto={u.foto}
                  className="size-8 text-xs"
                />
              ) : null}
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">
                  {`${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() ||
                    u.email}
                  {u.id === identity?.id ? (
                    <span className="text-muted-foreground font-normal">
                      {" "}
                      · {translate("nevora.usuarios.tu")}
                    </span>
                  ) : null}
                </p>
                <p className="text-sm text-muted-foreground truncate">
                  {u.email}
                </p>
                <p className="text-xs text-muted-foreground">
                  {translate("nevora.usuarios.ultimo_acceso")}:{" "}
                  {accesos === undefined
                    ? "…"
                    : accesos[String(u.user_id)]
                      ? formatoFechaHora(accesos[String(u.user_id)]!)
                      : translate("nevora.usuarios.nunca")}
                </p>
              </div>
              <Badge variant={u.administrator ? "default" : "outline"}>
                {translate(
                  u.administrator
                    ? "nevora.usuarios.administrador"
                    : "nevora.usuarios.comercial",
                )}
              </Badge>
              {u.disabled ? (
                <Badge variant="outline" className="text-muted-foreground">
                  {translate("nevora.usuarios.desactivado")}
                </Badge>
              ) : (
                <Badge variant="outline">
                  {translate("nevora.usuarios.activo")}
                </Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEdicion({ modo: "editar", usuario: u })}
                aria-label={`${translate("nevora.usuarios.editar")} ${u.email}`}
              >
                <Pencil className="size-3.5 mr-1" />
                {translate("nevora.usuarios.editar")}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {edicion ? (
        <DialogoUsuario
          edicion={
            edicion.modo === "editar"
              ? {
                  modo: "editar",
                  // La fila al día (p. ej. tras cambiar la foto sin cerrar).
                  usuario:
                    (usuarios ?? []).find((u) => u.id === edicion.usuario.id) ??
                    edicion.usuario,
                }
              : edicion
          }
          con30={con30}
          esUnoMismo={
            edicion.modo === "editar" && edicion.usuario.id === identity?.id
          }
          onCerrar={() => setEdicion(null)}
        />
      ) : null}
    </section>
  );
};

const DialogoUsuario = ({
  edicion,
  con30,
  esUnoMismo,
  onCerrar,
}: {
  edicion: Edicion;
  con30: boolean;
  esUnoMismo: boolean;
  onCerrar: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const alta = edicion.modo === "alta";
  const inicial = edicion.modo === "editar" ? edicion.usuario : undefined;

  const [datos, setDatos] = useState<DatosUsuario>({
    first_name: inicial?.first_name ?? "",
    last_name: inicial?.last_name ?? "",
    email: inicial?.email ?? "",
    administrator: inicial?.administrator ?? false,
    disabled: inicial?.disabled ?? false,
    // Por defecto, invitación por email (Robert, 09/10/2026: el SMTP ya funciona).
    modo: "invitacion",
    password: "",
    emailNuevo: "",
    emailRepetido: "",
  });
  const [comercial, setComercial] = useState<DatosPapelAlta>({
    rol: "setter",
    recibe: true,
    pct: "",
    tope: "",
    comision: "",
  });
  // 30: si no se puede borrar, qué tiene (para ofrecer desactivarlo).
  const [asociado, setAsociado] = useState<Record<string, number> | null>(null);
  const [errores, setErrores] = useState<ErroresUsuario>({});
  const [guardando, setGuardando] = useState(false);
  const cambiar = <K extends keyof DatosUsuario>(
    campo: K,
    valor: DatosUsuario[K],
  ) => setDatos((d) => ({ ...d, [campo]: valor }));

  const guardar = async () => {
    const encontrados = validarUsuario(datos, { alta, esUnoMismo });
    setErrores(encontrados);
    if (Object.keys(encontrados).length) return;
    setGuardando(true);
    try {
      if (alta) {
        const nuevo = await dataProvider.salesCreate({
          first_name: datos.first_name.trim(),
          last_name: datos.last_name.trim(),
          email: datos.email.trim().toLowerCase(),
          administrator: datos.administrator,
          disabled: false,
          password:
            datos.modo === "contrasena" ? (datos.password ?? "") : undefined,
        });
        // Auditoría 30/10/2026: su papel en el equipo, en el mismo paso.
        if (!datos.administrator && comercial.rol !== "ninguno" && nuevo?.id) {
          try {
            await dataProvider.configurarComercial(nuevo.id, {
              rol_comercial: comercial.rol,
              recibe_leads: comercial.rol === "setter" && comercial.recibe,
              porcentaje_reparto: aNumeroONulo(comercial.pct),
              tope_pendientes: aNumeroONulo(comercial.tope),
              comision_pct: aNumeroONulo(comercial.comision),
              team_lead_id: null,
            });
          } catch {
            notify("nevora.usuarios.papel_error", { type: "warning" });
          }
        }
      } else if (inicial) {
        const emailNuevo = (datos.emailNuevo ?? "").trim().toLowerCase();
        await dataProvider.salesUpdate(inicial.id, {
          first_name: datos.first_name.trim(),
          last_name: datos.last_name.trim(),
          administrator: datos.administrator,
          disabled: datos.disabled,
          ...(con30 && emailNuevo ? { email: emailNuevo } : {}),
        });
      }
      await queryClient.invalidateQueries({ queryKey: ["sales"] });
      notify(
        alta
          ? datos.modo === "invitacion"
            ? "nevora.usuarios.invitado"
            : "nevora.usuarios.creado"
          : "nevora.usuarios.guardado",
        { type: "success", messageArgs: { email: datos.email } },
      );
      onCerrar();
    } catch (error) {
      notify(claveErrorUsuarios(error), { type: "error" });
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async () => {
    if (!inicial) return;
    const nombre =
      `${inicial.first_name ?? ""} ${inicial.last_name ?? ""}`.trim() ||
      inicial.email;
    if (
      !window.confirm(translate("nevora.usuarios.borrar_confirmar", { nombre }))
    )
      return;
    setGuardando(true);
    try {
      await dataProvider.borrarUsuario(inicial.id);
      await queryClient.invalidateQueries({ queryKey: ["sales"] });
      notify("nevora.usuarios.borrado", { type: "success" });
      onCerrar();
    } catch (e) {
      const detalle = e as { code?: string; asociado?: Record<string, number> };
      if (detalle?.code === "tiene_datos") setAsociado(detalle.asociado ?? {});
      else notify(claveErrorUsuarios(e), { type: "error" });
    } finally {
      setGuardando(false);
    }
  };

  const desactivarEnSuLugar = async () => {
    if (!inicial) return;
    setGuardando(true);
    try {
      await dataProvider.salesUpdate(inicial.id, { disabled: true });
      await queryClient.invalidateQueries({ queryKey: ["sales"] });
      notify("nevora.usuarios.desactivado_hecho", { type: "success" });
      onCerrar();
    } catch (e) {
      notify(claveErrorUsuarios(e), { type: "error" });
    } finally {
      setGuardando(false);
    }
  };

  const error = (campo: keyof DatosUsuario) =>
    errores[campo] ? (
      <p className="text-sm text-destructive">{translate(errores[campo]!)}</p>
    ) : null;

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {translate(
              alta ? "nevora.usuarios.nuevo" : "nevora.usuarios.editar_titulo",
            )}
          </DialogTitle>
          <DialogDescription>
            {alta
              ? translate("nevora.usuarios.alta_ayuda")
              : (inicial?.email ?? "")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {con30 && inicial ? <EditorFoto usuario={inicial} /> : null}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="usuario-nombre">
                {translate("nevora.usuarios.nombre")}
              </Label>
              <Input
                id="usuario-nombre"
                value={datos.first_name}
                onChange={(e) => cambiar("first_name", e.target.value)}
              />
              {error("first_name")}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="usuario-apellidos">
                {translate("nevora.usuarios.apellidos")}
              </Label>
              <Input
                id="usuario-apellidos"
                value={datos.last_name}
                onChange={(e) => cambiar("last_name", e.target.value)}
              />
            </div>
          </div>
          {alta ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="usuario-email">
                  {translate("nevora.usuarios.email")}
                </Label>
                <Input
                  id="usuario-email"
                  type="email"
                  autoComplete="off"
                  value={datos.email}
                  onChange={(e) => cambiar("email", e.target.value)}
                />
                {error("email")}
              </div>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium mb-1">
                  {translate("nevora.usuarios.acceso")}
                </legend>
                <RadioGroup
                  value={datos.modo}
                  onValueChange={(v) =>
                    cambiar("modo", v as DatosUsuario["modo"])
                  }
                >
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="invitacion" id="acceso-invitacion" />
                    <Label htmlFor="acceso-invitacion" className="font-normal">
                      {translate("nevora.usuarios.acceso_invitacion")}
                    </Label>
                  </div>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="contrasena" id="acceso-contrasena" />
                    <Label htmlFor="acceso-contrasena" className="font-normal">
                      {translate("nevora.usuarios.acceso_contrasena")}
                    </Label>
                  </div>
                </RadioGroup>
              </fieldset>
              {datos.modo === "contrasena" ? (
                <div className="space-y-1.5">
                  <Label htmlFor="usuario-contrasena">
                    {translate("nevora.usuarios.contrasena_provisional")}
                  </Label>
                  <InputContrasena
                    id="usuario-contrasena"
                    autoComplete="new-password"
                    value={datos.password}
                    onChange={(e) => cambiar("password", e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {translate("nevora.usuarios.contrasena_ayuda")}
                  </p>
                  {error("password")}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {translate("nevora.usuarios.invitacion_ayuda")}
                </p>
              )}
            </>
          ) : null}
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="usuario-admin">
              {translate("nevora.usuarios.administrador")}
            </Label>
            <Switch
              id="usuario-admin"
              checked={datos.administrator}
              disabled={esUnoMismo}
              onCheckedChange={(v) => cambiar("administrator", v)}
            />
          </div>
          {error("administrator")}
          {alta && !datos.administrator ? (
            // Auditoría 30/10/2026: el papel en el equipo se pone al invitar, no después en Equipo.
            <fieldset
              className="space-y-3 rounded-lg border border-border/60 p-3"
              data-testid="papel-alta"
            >
              <legend className="px-1 text-sm font-medium">
                {translate("nevora.usuarios.papel")}
              </legend>
              <RadioGroup
                value={comercial.rol}
                onValueChange={(v) =>
                  setComercial((c) => ({ ...c, rol: v as PapelAlta }))
                }
                className="grid grid-cols-3 gap-2"
              >
                {PAPELES_ALTA.map((p) => (
                  <Label
                    key={p}
                    htmlFor={`papel-${p}`}
                    className="flex items-center gap-2 rounded-md border border-border/60 px-2.5 py-2 text-sm font-normal"
                  >
                    <RadioGroupItem id={`papel-${p}`} value={p} />
                    {translate(`nevora.usuarios.papeles.${p}`)}
                  </Label>
                ))}
              </RadioGroup>
              {comercial.rol === "setter" ? (
                <>
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="alta-recibe">
                      {translate("nevora.usuarios.recibe_leads")}
                    </Label>
                    <Switch
                      id="alta-recibe"
                      checked={comercial.recibe}
                      onCheckedChange={(v) =>
                        setComercial((c) => ({ ...c, recibe: v }))
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <CampoNumero
                      id="alta-pct"
                      etiqueta={translate("nevora.usuarios.pct_reparto")}
                      valor={comercial.pct}
                      cambiar={(v) => setComercial((c) => ({ ...c, pct: v }))}
                    />
                    <CampoNumero
                      id="alta-tope"
                      etiqueta={translate("nevora.usuarios.tope")}
                      valor={comercial.tope}
                      cambiar={(v) => setComercial((c) => ({ ...c, tope: v }))}
                    />
                  </div>
                </>
              ) : null}
              {comercial.rol !== "ninguno" ? (
                <CampoNumero
                  id="alta-comision"
                  etiqueta={translate("nevora.usuarios.comision")}
                  valor={comercial.comision}
                  cambiar={(v) => setComercial((c) => ({ ...c, comision: v }))}
                />
              ) : null}
              <p className="text-xs text-muted-foreground">
                {translate("nevora.usuarios.papel_ayuda")}
              </p>
            </fieldset>
          ) : null}
          {!alta && con30 ? (
            <fieldset className="space-y-2" data-testid="cambiar-email">
              <legend className="text-sm font-medium mb-1">
                {translate("nevora.usuarios.cambiar_email")}
              </legend>
              <div className="space-y-1.5">
                <Label htmlFor="usuario-email-nuevo">
                  {translate("nevora.usuarios.email_nuevo")}
                </Label>
                <Input
                  id="usuario-email-nuevo"
                  type="email"
                  autoComplete="off"
                  value={datos.emailNuevo}
                  onChange={(e) => cambiar("emailNuevo", e.target.value)}
                />
                {error("emailNuevo")}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="usuario-email-repetido">
                  {translate("nevora.usuarios.email_repetido")}
                </Label>
                <Input
                  id="usuario-email-repetido"
                  type="email"
                  autoComplete="off"
                  onPaste={(e) => e.preventDefault()}
                  value={datos.emailRepetido}
                  onChange={(e) => cambiar("emailRepetido", e.target.value)}
                />
                {error("emailRepetido")}
              </div>
              <p className="text-xs text-muted-foreground">
                {translate("nevora.usuarios.email_ayuda")}
              </p>
            </fieldset>
          ) : null}
          {!alta ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="usuario-desactivado">
                  {translate("nevora.usuarios.desactivado")}
                </Label>
                <Switch
                  id="usuario-desactivado"
                  checked={datos.disabled}
                  disabled={esUnoMismo}
                  onCheckedChange={(v) => cambiar("disabled", v)}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {translate(
                  esUnoMismo
                    ? "nevora.usuarios.uno_mismo_ayuda"
                    : "nevora.usuarios.desactivar_ayuda",
                )}
              </p>
              {error("disabled")}
            </>
          ) : null}
        </div>
        {asociado ? (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200 space-y-2"
            data-testid="no-se-puede-borrar"
          >
            <p>{translate("nevora.usuarios.no_se_puede_borrar")}</p>
            <ul className="list-disc pl-5">
              {lineasAsociado(asociado).map((l) => (
                <li key={l.clave}>
                  {translate(l.clave, { smart_count: l.n })}
                </li>
              ))}
            </ul>
            <Button
              size="sm"
              variant="outline"
              onClick={desactivarEnSuLugar}
              disabled={guardando}
            >
              {translate("nevora.usuarios.desactivar_en_su_lugar")}
            </Button>
          </div>
        ) : null}
        <DialogFooter className="sm:justify-between">
          {!alta && con30 && !esUnoMismo ? (
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={borrar}
              disabled={guardando}
              data-testid="borrar-usuario"
            >
              <Trash2 className="size-4 mr-1.5" />
              {translate("nevora.usuarios.borrar")}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCerrar} disabled={guardando}>
              {translate("ra.action.cancel")}
            </Button>
            <Button onClick={guardar} disabled={guardando}>
              {guardando ? (
                <Loader2 className="size-4 animate-spin mr-2" />
              ) : null}
              {translate(
                alta ? "nevora.usuarios.crear" : "nevora.usuarios.guardar",
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
